import clsx from 'clsx';
import { Flex, Text, Icon, iconWeight, Skeleton } from '@nula/core-ui';
import { usePlanByCode } from './use-subscription';
import { formatMoney } from './format';
import type { BillingInterval } from './types';
import styles from './team-plan-summary.module.css';

interface TeamPlanSummaryProps {
  planCode: string;
  interval: BillingInterval;
  selected: boolean;
  onSelect: (planCode: string) => void;
  recommended?: boolean;
}

/**
 * Selectable team-tier card for the create-team flow: shows the tier's per-seat
 * price + features and acts as a radio option. The price divides an annual total
 * by 12 (Stripe stores annual as the full-year amount).
 */
export function TeamPlanSummary({
  planCode,
  interval,
  selected,
  onSelect,
  recommended,
}: TeamPlanSummaryProps) {
  const { data: plan, isLoading } = usePlanByCode(planCode);

  if (isLoading) return <Skeleton active data-testid="team-plan-skeleton" />;
  if (!plan) return null;

  const entry = plan.prices.find((p) => p.interval === interval);
  const perSeat =
    entry && entry.amountCents !== null
      ? formatMoney(interval === 'year' ? Math.round(entry.amountCents / 12) : entry.amountCents, entry.currency)
      : null;

  return (
    <Flex
      vertical
      gap="sm"
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      className={clsx(styles.card, selected && styles.selected)}
      onClick={() => onSelect(planCode)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(planCode);
        }
      }}
    >
      {recommended && <span className={styles.ribbon}>Most popular</span>}

      <Flex align="baseline" justify="space-between">
        <Text size="lg" className={styles.name}>
          {plan.name}
        </Text>
        <Text className={styles.price}>
          {perSeat ? (
            <>
              {perSeat}
              <span className={styles.per}> / seat / mo</span>
            </>
          ) : (
            '—'
          )}
        </Text>
      </Flex>

      <Flex vertical gap="xs">
        {plan.features.map((f) => (
          <Flex key={f} gap="xs" align="start">
            <Icon name="check" size={16} weight={iconWeight.regular} className={styles.check} />
            <Text size="sm">{f}</Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
}
